/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 22/11/2022
 */

import React, { useEffect, useState } from 'react';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { useWorkplan } from '@/components/contexts/WorkplanContext/useWorkplan';
import { WorkplanVersion } from '@/wpt-lib/interfaces/Workplan';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { useGetRequest } from '@/utils/useStatefulRequests';

export type IWorkplanVersionContext = {
    version?: number;
    current: boolean;
    workplanVersion?: WorkplanVersion;
    loading: boolean;
    error: boolean;
    updateVersion: (changes: Partial<WorkplanVersion>) => void;
};

export const WorkplanVersionContext = createUnprovisionedContext<IWorkplanVersionContext>('WorkplanVersionContext');

const WorkplanVersionProvider = ({ version, children }: { version?: number; children: React.ReactNode }) => {
    const { workplan } = useWorkplan();

    const current = workplan?.currentVersion && version ? workplan.currentVersion.version === version : true;

    const { makeRequest, responseData, loading, error } = useGetRequest<WorkplanVersion>({});

    const [updates, setUpdates] = useState<Partial<WorkplanVersion>>({});

    useAsyncEffect(async () => {
        try {
            setUpdates({});
            if (workplan) {
                await makeRequest(`/workplan/${workplan.id}/version/${version ?? workplan.currentVersion.version}`);
            }
        } catch (e) {
            // Handled in hook
        }
    }, [version, workplan]);

    useEffect(() => {
        setUpdates({});
    }, [responseData]);

    return (
        <WorkplanVersionContext.Provider
            value={{
                version,
                current,
                workplanVersion: responseData ? { ...responseData, ...updates } : undefined,
                loading,
                error: !!error,
                updateVersion: (changes) => {
                    setUpdates({ ...updates, ...changes });
                },
            }}
        >
            {children}
        </WorkplanVersionContext.Provider>
    );
};

export default WorkplanVersionProvider;
